import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Shield, ShieldCheck, Smartphone, MapPin, Clock, LogOut,
  AlertTriangle, Activity, Laptop,
} from "lucide-react";
import { API, useAuth } from "../contexts/AuthContext";

/**
 * Student Security page — the "mirror" lever:
 *   • Shows the ONE currently active session (single-session policy)
 *   • Shows last N recognized devices
 *   • "Sign out everywhere + lock" action
 *   • Explains the single-session policy
 */
export default function SecurityPage() {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/security/my-session`),
        axios.get(`${API}/security/device-history`),
      ]);
      setSession(s.data?.session || null);
      setHistory(h.data?.items || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const signOutEverywhere = async () => {
    if (!window.confirm("Sign out on every device + force re-login here?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/security/sign-out-everywhere`);
      window.location.href = "/login?kicked=1";
    } catch {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 space-y-6" data-testid="security-page">
      {/* Header */}
      <div>
        <div className="portal-kicker mb-2">— Settings · Security</div>
        <h1 className="portal-page-title">Who's signed into your account.</h1>
        <p className="portal-page-sub max-w-xl mt-2">
          LogicsWallah enforces <strong style={{ color: "var(--px-ink)" }}>one active device</strong> per account to protect your paid access.
          Signing in on a new device automatically signs out the previous one.
        </p>
      </div>

      {/* Current session card */}
      <section className="lms-card p-6 relative overflow-hidden" data-testid="current-session-card">
        <div aria-hidden className="absolute -top-16 -right-16 w-52 h-52 rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, var(--accent-soft), transparent 70%)", filter: "blur(30px)" }} />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                 style={{
                   background: "var(--px-success-soft)",
                   border: "1px solid var(--px-success)",
                   color: "var(--px-success)",
                 }}>
              <ShieldCheck size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="lms-mono text-[10px] tracking-[0.18em] uppercase mb-1 flex items-center gap-1.5"
                   style={{ color: "var(--px-success)" }}>
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70"
                        style={{ background: "var(--px-success)" }} />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: "var(--px-success)" }} />
                </span>
                Currently active · This device
              </div>
              <div className="lms-display text-lg leading-tight" style={{ color: "var(--px-ink)" }}>
                {loading ? (
                  <span className="lms-shimmer inline-block w-48 h-5 rounded" />
                ) : session ? (
                  `${session.device_label} · ${session.browser_label}`
                ) : "No active session"}
              </div>
              {session && (
                <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px]"
                     style={{ color: "var(--px-ink-mute)" }}>
                  <span className="inline-flex items-center gap-1"><MapPin size={11} />{session.city || "—"}{session.country ? `, ${session.country}` : ""}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={11} />Signed in {niceRel(session.created_at)}</span>
                  <span className="inline-flex items-center gap-1 font-mono">{session.ip}</span>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={signOutEverywhere}
            disabled={busy}
            className="h-10 px-4 text-xs gap-1.5 inline-flex items-center justify-center whitespace-nowrap rounded-full font-semibold border transition"
            style={{
              background: "#e11d48",
              borderColor: "#be123c",
              color: "#fff",
              boxShadow: "0 6px 18px rgba(225,29,72,0.25)",
              opacity: busy ? 0.6 : 1,
            }}
            data-testid="sign-out-everywhere"
          >
            <LogOut size={13} /> {busy ? "Signing out..." : "Sign out everywhere"}
          </button>
        </div>
      </section>

      {/* Policy explainer */}
      <section className="rounded-2xl p-5"
               style={{
                 background: "var(--accent-soft)",
                 border: "1px solid var(--accent)",
               }}>
        <div className="flex items-start gap-3">
          <Shield size={18} style={{ color: "var(--accent-deep)" }} className="mt-0.5" />
          <div>
            <div className="lms-mono text-[10px] tracking-[0.18em] uppercase mb-1"
                 style={{ color: "var(--accent-deep)" }}>
              How single-device access works
            </div>
            <ul className="text-[13px] leading-relaxed space-y-1.5 list-disc list-inside"
                style={{ color: "var(--px-ink-soft)" }}>
              <li>You can be signed in on <strong style={{ color: "var(--px-ink)" }}>one device at a time</strong>.</li>
              <li>Logging in on a new device <strong style={{ color: "var(--px-ink)" }}>automatically signs out the previous one</strong>.</li>
              <li>You'll get an <strong style={{ color: "var(--px-ink)" }}>email alert</strong> for every new device sign-in.</li>
              <li>If a friend or stranger gets in, click <strong style={{ color: "var(--px-ink)" }}>"Sign out everywhere"</strong> and change your password immediately.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Recognized devices */}
      <section className="lms-card p-6" data-testid="device-history-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="portal-kicker mb-1">— Recognized devices</div>
            <h2 className="lms-display text-xl" style={{ color: "var(--px-ink)" }}>Last {history.length} sign-ins</h2>
          </div>
          <button onClick={load} className="portal-btn h-8 text-[11px]" data-testid="refresh-history">
            <Activity size={11} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map(i => <div key={i} className="lms-shimmer h-12 rounded-[10px]" />)}
          </div>
        ) : history.length === 0 ? (
          <p className="text-center py-6" style={{ color: "var(--px-ink-mute)" }}>No device history yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map((d, idx) => (
              <motion.div
                key={d.device_fp || idx}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-[12px]"
                style={{
                  background: "var(--px-surface-soft)",
                  border: "1px solid var(--px-line)",
                }}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                     style={{ background: "var(--accent-soft)", color: "var(--accent-deep)" }}>
                  {d.is_mobile ? <Smartphone size={15} /> : <Laptop size={15} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate" style={{ color: "var(--px-ink)" }}>{d.device_label} · {d.browser_label}</div>
                  <div className="text-[11px] flex items-center gap-2 mt-0.5 flex-wrap"
                       style={{ color: "var(--px-ink-mute)" }}>
                    <span className="inline-flex items-center gap-1"><MapPin size={10} />{d.last_city || "—"}</span>
                    <span>·</span>
                    <span>{niceRel(d.last_seen_at)}</span>
                  </div>
                </div>
                {session?.device_fp === d.device_fp && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ background: "var(--px-success-soft)", color: "var(--px-success)", border: "1px solid var(--px-success)" }}>
                    THIS DEVICE
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Watermark disclosure */}
      <section className="rounded-2xl p-5"
               style={{
                 background: "var(--px-warn-soft)",
                 border: "1px solid var(--px-warn)",
               }}>
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} style={{ color: "var(--px-warn)" }} className="mt-0.5" />
          <div>
            <div className="lms-mono text-[10px] tracking-[0.18em] uppercase mb-1" style={{ color: "var(--px-warn)" }}>
              — Video watermark notice
            </div>
            <p className="text-[13px] leading-relaxed" style={{ color: "var(--px-ink-soft)" }}>
              Every lecture you watch is watermarked with your email <span className="font-mono" style={{ color: "var(--px-ink)" }}>{user?.email}</span>
              {" "}and the last 4 digits of your phone. Re-distributed recordings can and will be traced back to the account of origin — leading to permanent termination.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function niceRel(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
